import _ from 'lodash';
import {
  useFeatureFlagEnabled as usePostHogFeatureFlagEnabled,
  useFeatureFlagPayload as usePostHogFeatureFlagPayload,
} from 'posthog-js/react';
import React from 'react';
import type * as TypeFest from 'type-fest';
import type { FeatureFlag } from '../flags';
import type { FeatureFlagPayload, FlaggrRelease } from '../types';

type PostHogPayload<F extends FeatureFlag> = TypeFest.Simplify<
  (FeatureFlagPayload<F> & { release: FlaggrRelease }) | null
>;

export interface UsePostHogFeatureFlagHook<F extends FeatureFlag> {
  enabled: boolean | null;
  release: FlaggrRelease | null;
  payload: FeatureFlagPayload<F> | null;
}

/**
 * A hook to retrieve the feature flag data from PostHog.
 *
 * @param feature A feature flag to check.
 * @returns The feature flag data.
 */
export const usePostHogFeatureFlag = <F extends FeatureFlag>(
  feature: F
): UsePostHogFeatureFlagHook<F> => {
  const enabled = usePostHogFeatureFlagEnabled(feature);
  const result = usePostHogFeatureFlagPayload(
    feature
  ) as PostHogPayload<F> | null;

  return React.useMemo(() => {
    if (_.isNil(result)) {
      return {
        enabled: enabled ?? null,
        release: 'stable',
        payload: null,
      };
    }
    const { release, ...payload } = result;
    return {
      enabled: enabled ?? null,
      release: release ?? 'stable',
      payload: payload as unknown as FeatureFlagPayload<F>,
    };
  }, [enabled, result]);
};
